// EndnoteRefNode — TipTap node for excerptus.endnoteRef.
// Authored exactly like a footnote (WYSIWYG popover body). At render time,
// endnoteRefs always collect into the trailing block; footnoteRefs respect
// ctx.paginated. Numbering is shared with footnotes (document order) — see
// render/document.ts.
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { footnoteBodyId, footnoteRefId } from "@/lib/briefDocument/footnoteAnchors";
import { footnoteBodyPlainText } from "@/lib/briefDocument/footnoteBody";
import { FootnoteBodyEditor } from "./FootnoteBodyEditor";
import { FootnoteAttachSource } from "./FootnoteAttachSource";

const EndnoteRefView = ({ node, updateAttributes }: NodeViewProps) => {
  const [open, setOpen] = useState(false);
  const body = (node.attrs.body as string) ?? "";
  const id = (node.attrs.id as string | null) ?? null;
  const refId = (node.attrs.refId as string | null) ?? null;
  const locator = (node.attrs.locator as string | null) ?? null;
  const preview = footnoteBodyPlainText(body).slice(0, 60);
  const jumpToBody = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!id) return;
    document.getElementById(footnoteBodyId(id))?.scrollIntoView({ behavior: "smooth", block: "center" });
    setOpen(false);
  };
  return (
    <NodeViewWrapper as="span" className="inline-block">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id ? footnoteRefId(id) : undefined}
            className="mx-0.5 inline-flex items-center rounded bg-accent/40 px-1.5 py-0.5 align-super text-xs font-medium text-foreground hover:bg-accent/60 scroll-mt-24"
            aria-label="Edit endnote"
          >
            [en{preview ? `: ${preview}${preview.length === 60 ? "…" : ""}` : ""}]
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-96 space-y-3">
          <FootnoteAttachSource
            refId={refId}
            locator={locator}
            onChange={(next) => updateAttributes(next)}
          />
          <div>
            <Label className="mb-2 block text-xs">Endnote body</Label>
            <FootnoteBodyEditor
              value={body}
              onChange={(next) => updateAttributes({ body: next })}
              ariaLabel="Endnote body"
              placeholder={
                refId
                  ? "Optional commentary appended after the citation (e.g. \"See also chapter 3…\")."
                  : undefined
              }
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground">
              Endnotes always collect at the end.
            </p>
            {id ? (
              <a
                href={`#${footnoteBodyId(id)}`}
                onClick={jumpToBody}
                className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Jump to body ↓
              </a>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
};

export const EndnoteRefNode = Node.create({
  name: "excerptus.endnoteRef",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-id"),
        renderHTML: (attrs) => ({ "data-id": attrs.id }),
      },
      body: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-body") ?? "",
        renderHTML: (attrs) => ({ "data-body": attrs.body }),
      },
      /** Optional library_items.id this endnote cites. */
      refId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-ref-id"),
        renderHTML: (attrs) => (attrs.refId ? { "data-ref-id": attrs.refId as string } : {}),
      },
      /** Optional locator (e.g. "p. 42") paired with refId. */
      locator: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-locator"),
        renderHTML: (attrs) => (attrs.locator ? { "data-locator": attrs.locator as string } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "span.endnote-ref" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "endnote-ref" })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(EndnoteRefView);
  },
});
